/**
 * AutoDetectPopup — the one-tap "AI saw this, want to list it?" sheet.
 *
 * Behaviour
 * - Slides in from the top of the seller's stage when the AutoDetect hook
 *   surfaces a candidate.
 * - Seller picks Buy Now or Auction (radio chips, Buy Now default per
 *   product spec).
 * - "List it" → POST /api/listings with the AI payload + chosen type,
 *   attached to the current stream. Closes on success with a toast.
 * - "Edit" → forwards the AI payload to the existing QuickAddListingModal
 *   pre-filled.
 * - "Dismiss" → notifies the hook so the same item won't re-prompt for
 *   60 s.
 */
import React, { useEffect, useState } from "react";
import { SparkleIcon, XIcon, CheckCircleIcon, StorefrontIcon, WaveformIcon } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function AutoDetectPopup({ candidate, streamId, onDismiss, onListed, onEdit }) {
  const [type, setType] = useState("fixed");
  const [busy, setBusy] = useState(false);

  // Reset chosen type whenever a new candidate appears, defaulting to the
  // model's suggestion (Buy Now for everything not in the auction-y
  // category list — see SYSTEM_PROMPT in routes_detect.py).
  useEffect(() => {
    if (candidate?.suggested_type === "auction") setType("auction");
    else setType("fixed");
  }, [candidate]);

  if (!candidate) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        title: candidate.title,
        description: candidate.reasoning || candidate.title,
        category: candidate.category || "Other",
        price: Number(candidate.suggested_price) || 0,
        type,
        quantity: 1,
        stream_id: streamId,
      };
      const r = await api.post("/listings", body);
      const listingId = r?.data?.id;
      toast.success(`Listed · $${body.price.toFixed(0)} ${type === "auction" ? "Auction" : "Buy Now"}`);
      onListed && onListed(listingId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not list — try Edit instead");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="absolute left-1/2 -translate-x-1/2 z-30 w-[min(94vw,460px)] rounded-2xl bg-zinc-950/98 backdrop-blur border border-amber-400/40 shadow-2xl text-white"
      style={{
        // Sit comfortably BELOW the End Stream pill on mobile (which is
        // anchored at `safe-area + 12px` with a 36px-tall button). 64px
        // of headroom keeps a clean gap on every device. On desktop the
        // End Stream button is in the same lane, so the same offset
        // also works without overlap.
        top: "calc(max(var(--sai-top, env(safe-area-inset-top, 0px)), 28px) + 64px)",
      }}
      data-testid="auto-detect-popup"
      role="dialog"
      aria-label="AI detected item"
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10">
        <span className="h-6 w-6 rounded-full bg-gradient-to-br from-amber-300 to-orange-500 grid place-items-center text-zinc-950">
          <SparkleIcon size={13} weight="fill" />
        </span>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.16em] text-amber-300 font-black leading-none">
            Bidoria sees an item
          </div>
          <div className="text-[10px] text-zinc-300 leading-none mt-0.5">
            {candidate.confidence}% sure · tap to list
          </div>
        </div>
        <button
          type="button"
          onClick={() => onDismiss && onDismiss(candidate.item_signature)}
          className="h-7 w-7 rounded-full grid place-items-center text-zinc-400 hover:bg-white/10 hover:text-white"
          data-testid="auto-detect-dismiss"
          aria-label="Dismiss"
        >
          <XIcon size={14} weight="bold" />
        </button>
      </div>

      <div className="p-3 space-y-2.5">
        <div>
          <div className="text-[15px] font-black tracking-tight leading-tight" data-testid="auto-detect-title">
            {candidate.title}
          </div>
          <div className="text-[11px] text-zinc-200 leading-snug mt-1" data-testid="auto-detect-reasoning">
            {candidate.category} · {candidate.reasoning}
          </div>
        </div>

        {/* Buy-Now vs Auction toggle. The model's suggestion is preselected
            but the seller can override before listing. */}
        <div
          className="grid grid-cols-2 gap-1.5 rounded-xl bg-white/5 p-1"
          data-testid="auto-detect-type-toggle"
        >
          <button
            type="button"
            onClick={() => setType("fixed")}
            className={`flex items-center justify-center gap-1.5 h-9 rounded-lg text-[12px] font-bold transition ${
              type === "fixed" ? "bg-white text-zinc-950" : "text-zinc-300 hover:text-white"
            }`}
            data-testid="auto-detect-type-fixed"
          >
            <StorefrontIcon size={14} weight="fill" />
            Buy Now
          </button>
          <button
            type="button"
            onClick={() => setType("auction")}
            className={`flex items-center justify-center gap-1.5 h-9 rounded-lg text-[12px] font-bold transition ${
              type === "auction" ? "bg-white text-zinc-950" : "text-zinc-300 hover:text-white"
            }`}
            data-testid="auto-detect-type-auction"
          >
            <WaveformIcon size={14} weight="fill" />
            Auction
          </button>
        </div>

        <div className="flex items-center justify-between rounded-xl bg-white/10 px-3 py-2">
          <div className="text-[11px] uppercase tracking-wide text-zinc-200 font-bold">
            {type === "auction" ? "Starting bid" : "Price"}
          </div>
          <div className="text-lg font-black text-amber-300 tracking-tight" data-testid="auto-detect-price">
            ${Number(candidate.suggested_price || 0).toFixed(0)}
          </div>
        </div>

        <div className="grid grid-cols-[1fr_auto] gap-2 pt-1">
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="h-11 rounded-full bg-gradient-to-r from-amber-300 via-amber-400 to-orange-500 hover:from-amber-200 hover:to-orange-400 text-zinc-950 font-black tracking-tight text-sm shadow-lg disabled:opacity-60"
            data-testid="auto-detect-confirm"
          >
            {busy ? "Listing…" : (
              <span className="inline-flex items-center gap-1.5">
                <CheckCircleIcon size={16} weight="fill" />
                {type === "auction" ? "Start auction" : "List it"}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => onEdit && onEdit({ ...candidate, type })}
            className="h-11 px-4 rounded-full bg-white/10 hover:bg-white/15 text-white text-sm font-bold"
            data-testid="auto-detect-edit"
          >
            Edit
          </button>
        </div>
      </div>
    </div>
  );
}
